import { useEffect } from 'react';
import { getBrandAndHotelData } from '../helpers/handleBrandAndHotelData';
import { getInitialHotelSelectionData } from '../helpers/hotelSelectorHelpers';
import Analytics from './analytics';
import { getItem } from './localStorage';

export const MYLQA_ANALYTICS_EVENTS = {
  MYACTIONS_PAGE_VIEWED: 'MYACTIONS: PAGE VIEWED',
  ACTION_PLAN_LIST_PAGE_VIEWED: 'MYACTIONS ACTION PLAN LIST: PAGE VIEWED',
  SELF_ASSESSMENTS_PAGE_VIEWED: 'MYACTIONS SELF ASSESSMENTS: PAGE VIEWED',
  MYAUDIT_PAGE_VIEWED: 'MYACTIONS MYAUDIT: PAGE VIEWED',
  SESSION_START: 'USER: SESSION START',
} as const;

export type MyLqaAnalyticsEvent =
  (typeof MYLQA_ANALYTICS_EVENTS)[keyof typeof MYLQA_ANALYTICS_EVENTS];

export interface MyLqaEventProperties {
  brandName: string;
  brandId: number | undefined;
  hotelName: string;
  hotelId: number | undefined;
  userId: number | undefined;
  userManagement: boolean | undefined;
  userType: string | undefined;
}

interface StoredMyLqaUser {
  id?: number;
  userType?: string;
  isUserManagementAllowed?: boolean;
}

function readStoredUser (): StoredMyLqaUser | undefined {
  const stored = getItem('user');
  const user = stored && stored.user;
  if (!user || typeof user !== 'object') {
    return undefined;
  }

  return {
    id: typeof user.id === 'number' ? user.id : undefined,
    userType: typeof user.userType === 'string' ? user.userType : undefined,
    isUserManagementAllowed:
      typeof user.isUserManagementAllowed === 'boolean'
        ? user.isUserManagementAllowed
        : undefined,
  };
}

export function getMyLqaEventProperties (): MyLqaEventProperties {
  const user = readStoredUser();
  const allHotels = getBrandAndHotelData() || [];
  const { brandName, hotelName, selectedHotel } =
    getInitialHotelSelectionData(allHotels);

  return {
    brandName,
    brandId: selectedHotel ? selectedHotel.brandId : undefined,
    hotelName,
    hotelId: selectedHotel ? selectedHotel.id : undefined,
    userId: user ? user.id : undefined,
    userManagement: user ? user.isUserManagementAllowed : undefined,
    userType: user ? user.userType : undefined,
  };
}

export function trackMyLqaEvent (eventName: MyLqaAnalyticsEvent): void {
  Analytics.sendEvent(eventName, getMyLqaEventProperties());
}

export function trackSessionStart (): void {
  trackMyLqaEvent(MYLQA_ANALYTICS_EVENTS.SESSION_START);
}

export function useTrackMyLqaPageView (
  eventName?: MyLqaAnalyticsEvent
): void {
  useEffect(() => {
    if (!eventName) {
      return;
    }
    trackMyLqaEvent(eventName);
  }, [eventName]);
}
